/**
 * Route map for cross-page navigation.
 *
 * Each entry describes:
 *   - parent:    optional "back to …" link (renders breadcrumb-style)
 *   - next:      suggested next page (renders the "Up next" card)
 *   - secondary: optional second suggestion in the "Up next" row
 *   - title:     short label used when this route is shown as a parent/next/secondary
 *
 * Catch-all dynamic routes (e.g. /network-proof/:id) are matched with a prefix
 * check. The most specific match wins.
 */
export const ROUTE_MAP = {
  "/": {
    title: "Home",
    next: { path: "/about", title: "What is Circularity Global?" },
    secondary: { path: "/atlas", title: "Open the Atlas" },
  },
  "/about": {
    title: "What is Circularity Global?",
    parent: { path: "/", title: "Home" },
    next: { path: "/network", title: "The Network" },
    secondary: { path: "/ai-tools", title: "AI Tools" },
  },
  "/network": {
    title: "The Network",
    parent: { path: "/about", title: "About" },
    next: { path: "/hubs", title: "Hub Library" },
    secondary: { path: "/network-proof", title: "Network Proof" },
  },
  "/hubs": {
    title: "Hub Library",
    parent: { path: "/network", title: "The Network" },
    next: { path: "/ai-tools/hub-builder", title: "Build a hub" },
    secondary: { path: "/atlas", title: "Where to build" },
  },
  "/marketplace": {
    title: "Marketplace",
    parent: { path: "/network", title: "The Network" },
    next: { path: "/atlas", title: "Find opportunities by geography" },
    secondary: { path: "/contact", title: "Join the network" },
  },
  "/atlas": {
    title: "Atlas",
    parent: { path: "/", title: "Home" },
    next: { path: "/intelligence", title: "Resource Intelligence" },
    secondary: { path: "/ai-tools/hub-builder", title: "Build a hub" },
  },
  "/intelligence": {
    title: "Resource Intelligence",
    parent: { path: "/", title: "Home" },
    next: { path: "/intelligence/indexes/plastic", title: "Plastic Index deep dive" },
    secondary: { path: "/methodology", title: "How it's calculated" },
  },
  "/intelligence/indexes/": {
    // prefix match for any /intelligence/indexes/:id
    title: "Index detail",
    parent: { path: "/intelligence", title: "Resource Intelligence" },
    next: { path: "/atlas", title: "See places where this works" },
    secondary: { path: "/methodology", title: "Methodology" },
  },
  "/methodology": {
    title: "Methodology Center",
    parent: { path: "/intelligence", title: "Resource Intelligence" },
    next: { path: "/network-proof", title: "Network Proof" },
    secondary: { path: "/about", title: "About Circularity" },
  },
  "/network-proof": {
    title: "Network Proof",
    parent: { path: "/", title: "Home" },
    next: { path: "/network-proof/maccia", title: "MACCIA 100-Village Pilot" },
    secondary: { path: "/foundation", title: "Foundation" },
  },
  "/network-proof/maccia": {
    title: "MACCIA + GTU pilot",
    parent: { path: "/network-proof", title: "Network Proof" },
    next: { path: "/foundation/apply", title: "Apply via Foundation" },
    secondary: { path: "/contact", title: "Talk to partnerships" },
  },
  "/network-proof/": {
    // catch-all for /network-proof/:id detail pages
    title: "Announcement",
    parent: { path: "/network-proof", title: "Network Proof" },
    next: { path: "/atlas", title: "Open the Atlas" },
    secondary: { path: "/contact", title: "Contact the team" },
  },
  "/foundation": {
    title: "Circularity Foundation",
    parent: { path: "/", title: "Home" },
    next: { path: "/foundation/apply", title: "Apply or partner" },
    secondary: { path: "/academy", title: "Academy" },
  },
  "/foundation/apply": {
    title: "Foundation application",
    parent: { path: "/foundation", title: "Foundation" },
    next: { path: "/academy", title: "Academy" },
    secondary: { path: "/", title: "Home" },
  },
  "/academy": {
    title: "Academy",
    parent: { path: "/", title: "Home" },
    next: { path: "/ai-tools", title: "AI Tools" },
    secondary: { path: "/foundation", title: "Foundation" },
  },
  "/credits": {
    title: "Credits Ledger",
    parent: { path: "/", title: "Home" },
    next: { path: "/dpp", title: "Product Passports" },
    secondary: { path: "/methodology", title: "Methodology" },
  },
  "/dpp": {
    title: "Product Passports",
    parent: { path: "/", title: "Home" },
    next: { path: "/credits", title: "Credits Ledger" },
    secondary: { path: "/marketplace", title: "Marketplace" },
  },
  "/ai-tools": {
    title: "AI Tools",
    parent: { path: "/", title: "Home" },
    next: { path: "/ai-tools/waste-to-resource", title: "Waste-to-Resource Engine" },
    secondary: { path: "/ai-tools/hub-builder", title: "Hub Builder" },
  },
  "/ai-tools/waste-to-resource": {
    title: "Waste-to-Resource Engine",
    parent: { path: "/ai-tools", title: "AI Tools" },
    next: { path: "/ai-tools/hub-builder", title: "Build a hub" },
    secondary: { path: "/marketplace", title: "Find buyers" },
  },
  "/ai-tools/hub-builder": {
    title: "Hub Builder",
    parent: { path: "/ai-tools", title: "AI Tools" },
    next: { path: "/ai-tools/calculators", title: "Run the calculators" },
    secondary: { path: "/atlas", title: "Pick a geography" },
  },
  "/ai-tools/opportunity-explorer": {
    title: "Opportunity Explorer",
    parent: { path: "/ai-tools", title: "AI Tools" },
    next: { path: "/atlas", title: "Open the Atlas" },
    secondary: { path: "/ai-tools/hub-builder", title: "Build a hub" },
  },
  "/ai-tools/digital-twin": {
    title: "Digital Twin",
    parent: { path: "/ai-tools", title: "AI Tools" },
    next: { path: "/ai-tools/calculators", title: "Calculators" },
    secondary: { path: "/atlas", title: "Atlas" },
  },
  "/ai-tools/calculators": {
    title: "Calculators",
    parent: { path: "/ai-tools", title: "AI Tools" },
    next: { path: "/portal/preferences", title: "Investor preferences" },
    secondary: { path: "/contact", title: "Talk to us" },
  },
  "/contact": {
    title: "Contact / Join",
    parent: { path: "/", title: "Home" },
    next: { path: "/dashboard", title: "Your dashboard" },
    secondary: { path: "/network", title: "Explore the network" },
  },
  "/dashboard": {
    title: "Your Dashboard",
    parent: { path: "/", title: "Home" },
    next: { path: "/portal/preferences", title: "Investor preferences" },
    secondary: { path: "/marketplace", title: "Marketplace" },
  },
  "/portal": {
    title: "Investor Portal",
    parent: { path: "/dashboard", title: "Dashboard" },
    next: { path: "/portal/preferences", title: "Set preferences" },
    secondary: { path: "/portal/kyc", title: "KYC" },
  },
  "/portal/preferences": {
    title: "Investor Preferences",
    parent: { path: "/portal", title: "Investor Portal" },
    next: { path: "/portal", title: "Back to portal" },
    secondary: { path: "/intelligence", title: "Resource Intelligence" },
  },
  "/portal/kyc": {
    title: "KYC",
    parent: { path: "/portal", title: "Investor Portal" },
    next: { path: "/portal/preferences", title: "Preferences" },
    secondary: { path: "/portal", title: "Back to portal" },
  },
  "/portal/history": {
    title: "Engine history",
    parent: { path: "/dashboard", title: "Dashboard" },
    next: { path: "/ai-tools", title: "Run another engine" },
    secondary: { path: "/", title: "Home" },
  },
  "/for/": {
    // catch-all for audience pages /for/governments, /for/landowners, etc.
    title: "Audience page",
    parent: { path: "/", title: "Home" },
    next: { path: "/contact", title: "Join the network" },
    secondary: { path: "/network", title: "Explore the network" },
  },
  "/admin": {
    title: "Admin",
    parent: { path: "/", title: "Home" },
    next: { path: "/admin/intelligence", title: "Intelligence CMS" },
  },
  "/admin/intelligence": {
    title: "Admin · Intelligence CMS",
    parent: { path: "/admin", title: "Admin" },
    next: { path: "/admin", title: "Back to admin" },
  },
  "/legal/": {
    title: "Legal",
    parent: { path: "/", title: "Home" },
    next: { path: "/contact", title: "Contact us" },
    secondary: { path: "/", title: "Home" },
  },
  "/login": {
    title: "Sign in",
    parent: { path: "/", title: "Home" },
    next: { path: "/register", title: "Create an account" },
    secondary: { path: "/contact", title: "Contact us" },
  },
  "/register": {
    title: "Create account",
    parent: { path: "/", title: "Home" },
    next: { path: "/login", title: "Sign in" },
    secondary: { path: "/contact", title: "Contact us" },
  },
  "/forgot-password": {
    title: "Forgot password",
    parent: { path: "/login", title: "Sign in" },
    next: { path: "/login", title: "Back to sign in" },
  },
  "/reset-password": {
    title: "Reset password",
    parent: { path: "/login", title: "Sign in" },
    next: { path: "/login", title: "Back to sign in" },
  },

  // ─── Intelligence (extended) ────────────────────────────────────────
  "/intelligence/markets": {
    title: "Markets · Prices & Indexes",
    parent: { path: "/intelligence", title: "Intelligence" },
    next: { path: "/intelligence/graphql", title: "GraphQL API" },
    secondary: { path: "/methodology", title: "Methodology" },
  },
  "/intelligence/explore": {
    title: "Knowledge Graph",
    parent: { path: "/intelligence", title: "Intelligence" },
    next: { path: "/intelligence/opportunities", title: "Opportunities" },
    secondary: { path: "/atlas", title: "Atlas" },
  },
  "/intelligence/communities": {
    title: "Communities",
    parent: { path: "/intelligence", title: "Intelligence" },
    next: { path: "/intelligence/materials", title: "Materials" },
  },
  "/intelligence/materials": {
    title: "Materials",
    parent: { path: "/intelligence", title: "Intelligence" },
    next: { path: "/intelligence/technologies", title: "Technologies" },
  },
  "/intelligence/technologies": {
    title: "Technologies",
    parent: { path: "/intelligence", title: "Intelligence" },
    next: { path: "/intelligence/capital", title: "Capital" },
  },
  "/intelligence/capital": {
    title: "Capital Ecosystem",
    parent: { path: "/intelligence", title: "Intelligence" },
    next: { path: "/intelligence/opportunities", title: "Opportunities" },
  },
  "/intelligence/opportunities": {
    title: "Opportunities",
    parent: { path: "/intelligence", title: "Intelligence" },
    next: { path: "/intelligence/markets", title: "Markets" },
  },
  "/intelligence/graphql": {
    title: "GraphQL API",
    parent: { path: "/intelligence", title: "Intelligence" },
    next: { path: "/atlas", title: "Atlas" },
    secondary: { path: "/methodology", title: "Methodology" },
  },
  "/intelligence/community/": {
    title: "Community passport",
    parent: { path: "/intelligence/communities", title: "Communities" },
    next: { path: "/intelligence/materials", title: "Browse materials" },
  },
  "/intelligence/material/": {
    title: "Material passport",
    parent: { path: "/intelligence/materials", title: "Materials" },
    next: { path: "/intelligence/technologies", title: "Technologies that use it" },
  },
  "/intelligence/technology/": {
    title: "Technology passport",
    parent: { path: "/intelligence/technologies", title: "Technologies" },
    next: { path: "/intelligence/capital", title: "Capital to fund it" },
  },
  "/intelligence/capital/passport/": {
    title: "Capital provider passport",
    parent: { path: "/intelligence/capital", title: "Capital" },
    next: { path: "/intelligence/opportunities", title: "Opportunities to fund" },
  },
  "/intelligence/opportunity/": {
    title: "Opportunity passport",
    parent: { path: "/intelligence/opportunities", title: "Opportunities" },
    next: { path: "/intelligence/markets", title: "Markets context" },
  },
};

/**
 * Resolve route metadata for a pathname.
 *  - Exact match wins.
 *  - Otherwise the longest matching prefix in ROUTE_MAP whose key ends with "/" wins.
 */
export function lookupRoute(pathname) {
  if (!pathname) return null;
  if (ROUTE_MAP[pathname]) return ROUTE_MAP[pathname];
  // Try longest prefix matches with trailing slash
  const prefixes = Object.keys(ROUTE_MAP)
    .filter((k) => k.endsWith("/"))
    .sort((a, b) => b.length - a.length);
  for (const p of prefixes) {
    if (pathname.startsWith(p)) return ROUTE_MAP[p];
  }
  return null;
}
